import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Upload, Check, X, AlertTriangle, CheckCircle2, Clipboard,
  FileText, ExternalLink, Zap, Info, RefreshCw, Eye, Ban,
  ChevronRight, ChevronDown, DollarSign, Calendar, Building2,
  Search, Trash2, PlayCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Spinner, EmptyState, KPI,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

// =============================================================================
// Bank reconciliation — /ap/reconcile
// =============================================================================
// Flow:
//   1. Paste a block of bank transactions (one row per line, tab-separated or
//      whitespace-separated). We parse dates, descriptions, amounts.
//   2. For each parsed row, call app.match_bank_txn() to score AP invoices.
//   3. Show all rows with auto-selected best match per row (if confidence ≥75).
//   4. User reviews, can click any row to see PDF preview side-by-side.
//   5. Click Confirm → bulk-marks all confirmed matches as paid via
//      app.confirm_bank_reconciliation RPC.
//
// The bulk operation is atomic — if any row fails, the whole batch rolls back.
// =============================================================================

// Confidence thresholds
const HIGH_CONFIDENCE = 75;
const POSSIBLE_MATCH  = 40;

type ParsedTxn = {
  id: string;          // local ID (not from DB yet)
  dbId?: string;       // bank_txn_id once saved
  date: string;        // ISO yyyy-mm-dd
  description: string;
  amount: number;      // negative = money out
  raw: string;         // original pasted line
};

type MatchCandidate = {
  ap_invoice_id: string;
  supplier_name: string;
  invoice_number: string;
  amount_inc_gst: number;
  due_date: string | null;
  status: string;
  confidence: number;
  reasons: string[];
};

type RowState = {
  txn: ParsedTxn;
  matches: MatchCandidate[];
  selectedApInvoiceId: string | null;   // null = skip / ignore
  action: "match" | "skip";             // whether to include in the batch
};

// -----------------------------------------------------------------------------
// Bank txn paste parser
// -----------------------------------------------------------------------------
// Xero's bank feed copy-paste lands looking roughly like:
//   25/03/2026  PAYMENT BORGER CRANE HIRE INV 1473045    -5,541.76
// This parser is forgiving: finds a date, an amount at the end, everything
// else becomes the description.
//
// Supported date formats: dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd, dd/mm/yy
// Amount can have $ sign, commas, parentheses (for negatives), minus prefix.
function parseTxnBlock(text: string): ParsedTxn[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const out: ParsedTxn[] = [];

  for (const line of lines) {
    // Find an amount at the end of the line (most reliable anchor)
    // Matches: -5,541.76  or  $5,541.76  or  (5,541.76)  or  5541.76
    const amtMatch = line.match(/([\(\-]?\$?-?[\d,]+(?:\.\d{1,2})?\)?)\s*$/);
    if (!amtMatch) continue;

    const amtRaw = amtMatch[1];
    const amtNeg = amtRaw.startsWith("(") && amtRaw.endsWith(")") || amtRaw.includes("-");
    const amt = parseFloat(amtRaw.replace(/[\$\(\)\-,\s]/g, ""));
    if (!Number.isFinite(amt)) continue;
    const amount = amtNeg ? -amt : amt;

    const rest = line.slice(0, line.length - amtMatch[0].length).trim();

    // Find a date at the start
    const dateMatch = rest.match(/^(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}-\d{2}-\d{2})/);
    if (!dateMatch) continue;
    const dateRaw = dateMatch[1];
    const isoDate = normaliseDate(dateRaw);
    if (!isoDate) continue;

    const description = rest.slice(dateRaw.length).trim().replace(/^[\t\s\-\|]+/, "");
    if (!description) continue;

    out.push({
      id: `local_${Math.random().toString(36).slice(2, 10)}`,
      date: isoDate,
      description,
      amount,
      raw: line,
    });
  }

  return out;
}

function normaliseDate(raw: string): string | null {
  // Already ISO?
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  // dd/mm/yyyy or dd-mm-yyyy or dd/mm/yy
  const m = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (!m) return null;

  const [, d, mo, y] = m;
  const year = y.length === 2 ? `20${y}` : y;
  return `${year}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

// -----------------------------------------------------------------------------
// Main screen
// -----------------------------------------------------------------------------

export function BankReconcileScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [pasteText, setPasteText] = useState("");
  const [rows, setRows] = useState<RowState[]>([]);
  const [isMatching, setIsMatching] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [batchName, setBatchName] = useState(
    () => `Bank rec ${new Date().toISOString().slice(0, 10)}`
  );
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const canConfirm = hasRole(user, "manager");

  // Pending AP invoices (reference count for the "waiting to be paid" KPI)
  const { data: pendingInvoices = [] } = useQuery({
    queryKey: ["ap_pending_payment"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, supplier_name, invoice_number, amount_inc_gst, due_date, status, pdf_storage_path, pdf_original_filename")
        .in("status", ["entered_in_xero", "approved"])
        .order("due_date", { ascending: true, nullsFirst: false });
      return (data ?? []) as any[];
    },
  });

  // --------------------------------------------------------------------------
  // Parse + match
  // --------------------------------------------------------------------------

  async function handleParseAndMatch() {
    setConfirmError(null);
    const parsed = parseTxnBlock(pasteText);
    if (parsed.length === 0) {
      setConfirmError("Couldn't parse any bank transactions from that block. Each line needs a date and an amount.");
      return;
    }

    setIsMatching(true);
    try {
      // 1. Insert all parsed rows into bank_transactions so we have real IDs
      //    to pass to match_bank_txn. We use a single batch insert.
      const insertPayload = parsed.map((t) => ({
        txn_date:    t.date,
        description: t.description,
        amount:      t.amount,
        status:      "unmatched",
        captured_by: (user as any).user_id ?? (user as any).id ?? null,
      }));

      const { data: inserted, error: insErr } = await supabase
        .from("bank_transactions")
        .insert(insertPayload)
        .select("bank_txn_id, txn_date, description, amount");

      if (insErr) throw insErr;

      // 2. For each inserted row, fetch candidates via match_bank_txn RPC
      const withMatches: RowState[] = await Promise.all(
        (inserted ?? []).map(async (ins: any, idx: number) => {
          const { data: matches, error: matchErr } = await supabase.schema('app').rpc(
            "match_bank_txn",
            { p_bank_txn_id: ins.bank_txn_id }
          );
          if (matchErr) console.warn("[match] rpc failed", matchErr);

          const candidates = (matches ?? []) as MatchCandidate[];
          // Auto-select top match only if high-confidence AND uniquely best
          const top = candidates[0];
          const second = candidates[1];
          const autoSelect =
            top && top.confidence >= HIGH_CONFIDENCE &&
            (!second || top.confidence - second.confidence >= 15);

          return {
            txn: {
              id: parsed[idx].id,
              dbId: ins.bank_txn_id,
              date: ins.txn_date,
              description: ins.description,
              amount: Number(ins.amount),
              raw: parsed[idx].raw,
            },
            matches: candidates,
            selectedApInvoiceId: autoSelect ? top.ap_invoice_id : null,
            action: "match",
          };
        })
      );

      setRows(withMatches);
      setPasteText(""); // clear the paste area now that it's parsed
    } catch (err: any) {
      setConfirmError(err.message ?? "Parsing failed");
    } finally {
      setIsMatching(false);
    }
  }

  // --------------------------------------------------------------------------
  // Confirm
  // --------------------------------------------------------------------------

  const confirmMut = useMutation({
    mutationFn: async () => {
      const matchesToConfirm = rows
        .filter((r) => r.action === "match" && r.selectedApInvoiceId && r.txn.dbId)
        .map((r) => ({ bank_txn_id: r.txn.dbId, ap_invoice_id: r.selectedApInvoiceId }));

      if (matchesToConfirm.length === 0) {
        throw new Error("Nothing selected to confirm. Pick a match on at least one row.");
      }

      // 1. Create the batch
      const totalAmount = rows
        .filter((r) => r.action === "match" && r.selectedApInvoiceId)
        .reduce((a, r) => a + Math.abs(r.txn.amount), 0);

      const { data: batch, error: batchErr } = await supabase
        .from("reconciliation_batches")
        .insert({
          batch_name:   batchName.trim() || `Batch ${new Date().toISOString()}`,
          source:       "paste",
          row_count:    rows.length,
          total_amount: totalAmount,
          created_by:   (user as any).user_id ?? (user as any).id ?? null,
        })
        .select("batch_id")
        .single();

      if (batchErr) throw batchErr;

      // 2. Mark skipped rows as 'ignored' before confirming matches
      const skipIds = rows
        .filter((r) => r.action === "skip" || !r.selectedApInvoiceId)
        .map((r) => r.txn.dbId)
        .filter(Boolean);

      if (skipIds.length > 0) {
        await supabase
          .from("bank_transactions")
          .update({ status: "ignored", batch_id: batch.batch_id })
          .in("bank_txn_id", skipIds);
      }

      // 3. Confirm the rest via RPC
      const { data: result, error: confirmErr } = await supabase.schema('app').rpc(
        "confirm_bank_reconciliation",
        {
          p_batch_id:     batch.batch_id,
          p_matches:      matchesToConfirm,
          p_confirmed_by: (user as any).user_id ?? (user as any).id ?? null,
        }
      );

      if (confirmErr) throw confirmErr;
      return result?.[0] ?? { matched_count: matchesToConfirm.length, total_paid: totalAmount };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ap_invoices"] });
      qc.invalidateQueries({ queryKey: ["ap_kpi"] });
      qc.invalidateQueries({ queryKey: ["ap_pending_payment"] });
      setRows([]);
      setSelectedRowId(null);
      setConfirmError(null);
    },
    onError: (err: any) => {
      setConfirmError(err.message ?? "Confirmation failed");
    },
  });

  // --------------------------------------------------------------------------
  // Row helpers
  // --------------------------------------------------------------------------

  function updateRow(id: string, patch: Partial<RowState>) {
    setRows((rs) => rs.map((r) => r.txn.id === id ? { ...r, ...patch } : r));
  }

  async function handleClearAll() {
    // Soft cleanup: mark still-unmatched local rows as ignored so they don't
    // leak into future batches. Safe if they were never persisted.
    const dbIds = rows.map((r) => r.txn.dbId).filter(Boolean) as string[];
    if (dbIds.length > 0) {
      await supabase
        .from("bank_transactions")
        .update({ status: "ignored" })
        .in("bank_txn_id", dbIds)
        .eq("status", "unmatched");
    }
    setRows([]);
    setSelectedRowId(null);
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const selectedRow = rows.find((r) => r.txn.id === selectedRowId) ?? null;
  const selectedMatch = selectedRow
    ? selectedRow.matches.find((m) => m.ap_invoice_id === selectedRow.selectedApInvoiceId) ?? null
    : null;

  const readyCount    = rows.filter((r) => r.action === "match" && r.selectedApInvoiceId).length;
  const needsReview   = rows.filter((r) => r.action === "match" && !r.selectedApInvoiceId).length;
  const ignoredCount  = rows.filter((r) => r.action === "skip").length;
  const totalSelected = rows
    .filter((r) => r.action === "match" && r.selectedApInvoiceId)
    .reduce((a, r) => a + Math.abs(r.txn.amount), 0);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      <button
        onClick={() => navigate("/ap")}
        className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4 hover:text-text"
      >
        <ArrowLeft size={14} /> Back to AP queue
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bank reconciliation</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Paste bank transactions from Xero or your bank feed. Ops finds matching AP invoices,
            you confirm, and they're marked paid in one go.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Pill
            label={`${pendingInvoices.length} invoices waiting to pay`}
            tone="mobilising"
          />
        </div>
      </div>

      {/* If no rows parsed yet: show paste box + pending invoices preview */}
      {rows.length === 0 ? (
        <>
          <Card>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">
                <Clipboard size={11} className="inline mr-1 align-[-1px]" />
                Paste bank transactions
              </div>
              <div className="text-[12px] text-text-muted mb-2">
                One transaction per line. Dates, descriptions and amounts are auto-detected.
                Works with copy-paste from Xero's bank feed or most bank portals.
              </div>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`Example:
25/03/2026  PAYMENT BORGER CRANE HIRE INV 1473045   -5,541.76
27/03/2026  DIRECT DEBIT M365 BUSINESS                  -89.00
28/03/2026  PAYMENT HARDLINE GROUP                   22,500.00`}
                className="w-full bg-ink-raised border border-line rounded p-3 text-[12px] text-text font-mono min-h-[180px] outline-none focus:border-accent/40"
                spellCheck={false}
              />

              {confirmError && (
                <div className="mt-3 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                  <AlertTriangle size={11} className="inline mr-1" />
                  {confirmError}
                </div>
              )}

              <div className="flex items-center justify-between mt-3">
                <div className="text-[11px] text-text-dim">
                  {pasteText.split(/\r?\n/).filter(Boolean).length} lines to parse
                </div>
                <Button
                  icon={PlayCircle}
                  onClick={handleParseAndMatch}
                  disabled={!pasteText.trim() || isMatching}
                >
                  {isMatching ? "Matching…" : "Parse & match"}
                </Button>
              </div>
            </div>
          </Card>

          {/* Pending invoices preview */}
          <Card className="mt-4">
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">
                Currently awaiting payment (in Xero)
              </div>

              {pendingInvoices.length === 0 ? (
                <EmptyState
                  title="No invoices awaiting payment"
                  description="Mark invoices as 'Entered in Xero' on the AP queue, then reconcile them here once paid."
                  icon={CheckCircle2}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="border-b border-line">
                        {["Invoice #", "Supplier", "Amount", "Due", "Status"].map((h) => (
                          <th key={h} className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingInvoices.map((inv: any) => {
                        const overdue = inv.due_date && inv.due_date < new Date().toISOString().slice(0, 10);
                        return (
                          <tr key={inv.ap_invoice_id} className="border-b border-line">
                            <td className="px-3 py-2 font-mono text-[11px] text-accent">{inv.invoice_number ?? "—"}</td>
                            <td className="px-3 py-2">{inv.supplier_name ?? "—"}</td>
                            <td className="px-3 py-2 font-mono">{fmtMoney(inv.amount_inc_gst)}</td>
                            <td className={`px-3 py-2 font-mono text-[11px] ${overdue ? "text-danger" : "text-text-muted"}`}>
                              {fmtDate(inv.due_date)}{overdue && " ⚠"}
                            </td>
                            <td className="px-3 py-2">
                              <Pill
                                label={inv.status === "entered_in_xero" ? "In Xero" : "Approved"}
                                tone={inv.status === "entered_in_xero" ? "onsite" : "mobilising"}
                                small
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        </>
      ) : (
        // Matching view: split layout
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-4">
          {/* LEFT: rows list */}
          <div>
            <Card>
              <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">
                    Matching {rows.length} transactions
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[11px] font-mono">
                    <span className="text-ok">{readyCount} ready</span>
                    {needsReview > 0 && <span className="text-warn">{needsReview} need review</span>}
                    {ignoredCount > 0 && <span className="text-text-dim">{ignoredCount} ignored</span>}
                    <span className="text-text">· {fmtMoney(totalSelected)} to settle</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                    placeholder="Batch name"
                    className="w-48"
                  />
                  <button
                    onClick={handleClearAll}
                    className="p-2 rounded-md text-text-muted hover:bg-ink-hover hover:text-danger border border-line"
                    title="Clear all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="divide-y divide-line">
                {rows.map((row) => (
                  <ReconcileRow
                    key={row.txn.id}
                    row={row}
                    selected={selectedRowId === row.txn.id}
                    onSelect={() => setSelectedRowId(row.txn.id)}
                    onChangeMatch={(apId) => updateRow(row.txn.id, { selectedApInvoiceId: apId })}
                    onToggleSkip={() => updateRow(row.txn.id, {
                      action: row.action === "skip" ? "match" : "skip",
                      selectedApInvoiceId: row.action === "skip" ? row.selectedApInvoiceId : null,
                    })}
                  />
                ))}
              </div>

              <div className="p-4 border-t border-line flex items-center justify-between flex-wrap gap-3">
                <Button variant="secondary" onClick={handleClearAll}>Discard</Button>

                <div className="flex items-center gap-2">
                  {confirmError && (
                    <span className="text-[11px] text-danger">
                      <AlertTriangle size={11} className="inline mr-1" />
                      {confirmError}
                    </span>
                  )}
                  <Button
                    icon={CheckCircle2}
                    onClick={() => confirmMut.mutate()}
                    disabled={!canConfirm || confirmMut.isPending || readyCount === 0}
                  >
                    {confirmMut.isPending ? "Confirming…" : `Confirm ${readyCount} match${readyCount === 1 ? "" : "es"}`}
                  </Button>
                </div>
              </div>

              {!canConfirm && (
                <div className="px-4 pb-3 text-[11px] text-text-muted">
                  Only managers + directors can confirm reconciliations.
                </div>
              )}
            </Card>
          </div>

          {/* RIGHT: detail panel for selected row */}
          <div>
            {selectedRow ? (
              <ReconcileDetail row={selectedRow} matchedInvoiceId={selectedRow.selectedApInvoiceId} />
            ) : (
              <Card>
                <div className="p-5 text-[12px] text-text-muted">
                  <Info size={14} className="inline mr-1 align-[-1px]" />
                  Click a row to see candidates + PDF preview.
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Row component
// -----------------------------------------------------------------------------
function ReconcileRow({
  row, selected, onSelect, onChangeMatch, onToggleSkip,
}: {
  row: RowState;
  selected: boolean;
  onSelect: () => void;
  onChangeMatch: (apId: string | null) => void;
  onToggleSkip: () => void;
}) {
  const best = row.matches[0];
  const selectedMatch = row.matches.find((m) => m.ap_invoice_id === row.selectedApInvoiceId) ?? null;
  const isMoneyOut = row.txn.amount < 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 transition-colors ${
        selected ? "bg-accent/5" : "hover:bg-ink-hover"
      } ${row.action === "skip" ? "opacity-40" : ""}`}
    >
      <div className="grid grid-cols-[120px_1fr_auto] gap-3 items-start">
        {/* Date + amount */}
        <div>
          <div className="font-mono text-[11px] text-text-muted">{fmtDate(row.txn.date)}</div>
          <div className={`font-mono text-[14px] mt-0.5 ${isMoneyOut ? "text-text" : "text-ok"}`}>
            {fmtMoney(Math.abs(row.txn.amount))}
          </div>
          <div className="text-[9px] font-mono uppercase text-text-dim">
            {isMoneyOut ? "money out" : "money in"}
          </div>
        </div>

        {/* Description + match */}
        <div className="min-w-0">
          <div className="text-[12px] text-text truncate">{row.txn.description}</div>

          <div className="mt-2">
            {row.action === "skip" ? (
              <Pill label="Ignored" tone="pending" small />
            ) : selectedMatch ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Pill
                  label={`Matched ${selectedMatch.confidence}%`}
                  tone={selectedMatch.confidence >= HIGH_CONFIDENCE ? "won" : "mobilising"}
                  small
                />
                <span className="text-[11px] text-text font-mono">{selectedMatch.invoice_number ?? "(no #)"}</span>
                <span className="text-[11px] text-text-muted truncate">{selectedMatch.supplier_name}</span>
              </div>
            ) : row.matches.length === 0 ? (
              <Pill label="No candidates" tone="lost" small />
            ) : (
              <Pill label={`${row.matches.length} candidate${row.matches.length === 1 ? "" : "s"} — review`} tone="pending" small />
            )}
          </div>

          {/* Candidate dropdown visible when row is selected */}
          {selected && row.action !== "skip" && row.matches.length > 0 && (
            <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()}>
              {row.matches.map((m) => (
                <label
                  key={m.ap_invoice_id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border ${
                    row.selectedApInvoiceId === m.ap_invoice_id
                      ? "border-accent/40 bg-accent/5"
                      : "border-line hover:bg-ink-hover"
                  }`}
                >
                  <input
                    type="radio"
                    name={`m-${row.txn.id}`}
                    checked={row.selectedApInvoiceId === m.ap_invoice_id}
                    onChange={() => onChangeMatch(m.ap_invoice_id)}
                    className="accent-accent"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] flex items-center gap-2">
                      <span className="font-mono text-accent">{m.invoice_number ?? "(no #)"}</span>
                      <span className="truncate">{m.supplier_name}</span>
                    </div>
                    <div className="text-[10px] text-text-muted font-mono">
                      {fmtMoney(m.amount_inc_gst)} · due {fmtDate(m.due_date)} · {m.reasons.join(" · ")}
                    </div>
                  </div>
                  <div className={`text-[11px] font-mono font-semibold ${
                    m.confidence >= HIGH_CONFIDENCE ? "text-ok" : "text-warn"
                  }`}>
                    {m.confidence}%
                  </div>
                </label>
              ))}
              {row.selectedApInvoiceId && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onChangeMatch(null); }}
                  className="text-[10px] text-text-muted hover:text-text px-2"
                >
                  ↺ Clear selection
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleSkip(); }}
            className={`p-1.5 rounded border text-[10px] ${
              row.action === "skip"
                ? "border-warn/40 text-warn"
                : "border-line text-text-muted hover:border-warn/40 hover:text-warn"
            }`}
            title={row.action === "skip" ? "Un-ignore" : "Ignore this row"}
          >
            <Ban size={12} />
          </button>
          {selected && <ChevronDown size={12} className="text-text-muted" />}
          {!selected && <ChevronRight size={12} className="text-text-muted" />}
        </div>
      </div>
    </button>
  );
}

// -----------------------------------------------------------------------------
// Detail panel — PDF preview + invoice detail
// -----------------------------------------------------------------------------
function ReconcileDetail({ row, matchedInvoiceId }: { row: RowState; matchedInvoiceId: string | null }) {
  const { data: inv } = useQuery({
    queryKey: ["ap_invoice_detail", matchedInvoiceId],
    enabled: !!matchedInvoiceId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        .select("*")
        .eq("ap_invoice_id", matchedInvoiceId!)
        .maybeSingle();
      return data as any;
    },
  });

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!inv?.pdf_storage_path) { setPdfUrl(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage
        .from("ap-documents")
        .createSignedUrl(inv.pdf_storage_path, 3600);
      if (!cancelled) setPdfUrl(data?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [inv?.pdf_storage_path]);

  return (
    <Card>
      <div className="p-4 border-b border-line">
        <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold mb-1">
          Bank transaction
        </div>
        <div className="text-[13px] text-text">{row.txn.description}</div>
        <div className="flex items-center gap-3 text-[11px] font-mono text-text-muted mt-1">
          <span>{fmtDate(row.txn.date)}</span>
          <span className={row.txn.amount < 0 ? "" : "text-ok"}>{fmtMoney(Math.abs(row.txn.amount))}</span>
        </div>
      </div>

      {!matchedInvoiceId ? (
        <div className="p-5 text-[12px] text-text-muted">
          {row.matches.length === 0
            ? "No AP invoices matched this transaction. It might not be a supplier payment — click 🚫 to ignore, or manually mark the invoice paid from the AP queue."
            : "Select a candidate on the left to preview its invoice PDF here."}
        </div>
      ) : !inv ? (
        <div className="p-5 flex justify-center"><Spinner /></div>
      ) : (
        <div className="p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold">
            Matched AP invoice
          </div>

          <div className="border border-line rounded-lg p-3 space-y-1.5 text-[12px]">
            <div className="grid grid-cols-[90px_1fr] gap-2">
              <span className="text-text-muted">Invoice #</span>
              <span className="font-mono text-accent">{inv.invoice_number ?? "(no number)"}</span>
            </div>
            <div className="grid grid-cols-[90px_1fr] gap-2">
              <span className="text-text-muted">Supplier</span>
              <span>{inv.supplier_name ?? "—"}</span>
            </div>
            <div className="grid grid-cols-[90px_1fr] gap-2">
              <span className="text-text-muted">Amount</span>
              <span className="font-mono">{fmtMoney(inv.amount_inc_gst)}</span>
            </div>
            <div className="grid grid-cols-[90px_1fr] gap-2">
              <span className="text-text-muted">Due</span>
              <span className="font-mono">{fmtDate(inv.due_date)}</span>
            </div>
            <div className="grid grid-cols-[90px_1fr] gap-2">
              <span className="text-text-muted">Budget</span>
              <span className="font-mono">{inv.budget_code ?? "—"}</span>
            </div>
            <div className="grid grid-cols-[90px_1fr] gap-2">
              <span className="text-text-muted">Status</span>
              <Pill label={inv.status} tone="onsite" small />
            </div>
          </div>

          {Math.abs(Number(inv.amount_inc_gst) - Math.abs(row.txn.amount)) > 0.01 && (
            <div className="bg-warn/10 border border-warn/30 rounded px-3 py-2 text-[11px] text-warn">
              <AlertTriangle size={11} className="inline mr-1" />
              Amount mismatch: invoice is {fmtMoney(inv.amount_inc_gst)}, bank row is {fmtMoney(Math.abs(row.txn.amount))}.
              Confirming anyway will mark this invoice paid at the full invoice amount.
            </div>
          )}

          {inv.pdf_storage_path ? (
            <>
              <a
                href={pdfUrl ?? "#"}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-ink-raised border border-line rounded hover:border-accent/40 text-[12px]"
              >
                <FileText size={14} className="text-accent" />
                <span className="flex-1 truncate">{inv.pdf_original_filename ?? "Open file"}</span>
                <ExternalLink size={11} className="text-text-muted" />
              </a>

              {pdfUrl && (
                <div className="border border-line rounded-lg overflow-hidden">
                  <iframe
                    src={pdfUrl}
                    title="invoice preview"
                    className="w-full h-[520px] bg-white"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="px-3 py-2 bg-ink-raised border border-line rounded text-[11px] text-text-muted">
              No PDF attached to this invoice.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
